import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Mail, Send, Loader2, Eye, Pencil, X } from 'lucide-react';
import logger from '../lib/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function InviteEmailDialog({ open, onClose, userData }) {
  const [subject, setSubject] = useState('');
  const [htmlContent, setHtmlContent] = useState('');
  const [customMessage, setCustomMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [editingSubject, setEditingSubject] = useState(false);
  const [previewMode, setPreviewMode] = useState(true);

  useEffect(() => {
    if (open && userData?.email) {
      fetchPreview();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, userData?.email]);

  const fetchPreview = async (msg) => {
    setLoading(true);
    try {
      const loginUrl = `${window.location.origin}/login`;
      const res = await axios.post(`${API_URL}/api/admin/users/invite-preview`, {
        name: userData.name,
        email: userData.email,
        role: userData.role,
        org_name: userData.org_name || '',
        login_url: loginUrl,
        custom_message: msg ?? customMessage,
      }, { withCredentials: true });
      setSubject(res.data.subject);
      setHtmlContent(res.data.html);
    } catch (err) {
      logger.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async () => {
    setSending(true);
    try {
      // If there's a custom message, inject it into the HTML
      let finalHtml = htmlContent;
      if (customMessage.trim()) {
        const customBlock = `<div style="background:#f0fdfa;border-left:3px solid #4ABFCF;padding:12px 16px;margin:16px 0;border-radius:0 6px 6px 0;"><p style="margin:0;color:#334155;font-size:14px;">${customMessage}</p></div>`;
        finalHtml = htmlContent.replace('<!-- Credentials Card -->', `${customBlock}\n    <!-- Credentials Card -->`);
      }

      await axios.post(`${API_URL}/api/admin/users/invite-email`, {
        email: userData.email,
        subject,
        html_content: finalHtml,
      }, { withCredentials: true });
      toast.success(`Invite email sent to ${userData.email}`);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send invite email');
    } finally {
      setSending(false);
    }
  };

  const handleSkip = () => {
    toast.info('Invite email skipped — user can still log in with default credentials');
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="invite-email-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-[#4ABFCF]" />
            Send Invite Email
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-4 pr-1">
          {/* Recipient */}
          <div className="flex items-center gap-3 p-3 rounded-lg bg-slate-50 border border-slate-100">
            <div className="text-xs text-slate-400 w-10">To:</div>
            <div className="text-sm font-medium text-[#0F2040]">{userData?.email}</div>
            <div className="ml-auto text-xs text-slate-400">{userData?.name}</div>
          </div>

          {/* Subject */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <Label className="text-xs text-slate-500">Subject</Label>
              <button
                type="button"
                onClick={() => setEditingSubject(!editingSubject)}
                className="text-[10px] text-[#4ABFCF] hover:underline flex items-center gap-1"
                data-testid="toggle-subject-edit"
              >
                <Pencil className="w-2.5 h-2.5" /> {editingSubject ? 'Done' : 'Edit'}
              </button>
            </div>
            {editingSubject ? (
              <Input
                value={subject}
                onChange={e => setSubject(e.target.value)}
                className="text-sm"
                data-testid="invite-subject-input"
              />
            ) : (
              <p className="text-sm text-[#0F2040] font-medium px-1" data-testid="invite-subject-display">{subject}</p>
            )}
          </div>

          {/* Custom Message */}
          <div>
            <Label className="text-xs text-slate-500 mb-1 block">Add a personal message <span className="text-slate-300">(optional)</span></Label>
            <textarea
              value={customMessage}
              onChange={e => setCustomMessage(e.target.value)}
              placeholder="e.g. Looking forward to working with you on the LMS implementation..."
              rows={2}
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring resize-none"
              data-testid="invite-custom-message"
            />
          </div>

          {/* Email Preview */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <Label className="text-xs text-slate-500 flex items-center gap-1"><Eye className="w-3 h-3" /> Email Preview</Label>
            </div>
            {loading ? (
              <div className="flex items-center justify-center py-12 bg-slate-50 rounded-lg border border-slate-100">
                <Loader2 className="w-5 h-5 animate-spin text-[#4ABFCF]" />
              </div>
            ) : (
              <div
                className="border border-slate-200 rounded-lg overflow-hidden bg-white"
                style={{ maxHeight: '340px', overflowY: 'auto' }}
                data-testid="invite-email-preview"
              >
                <iframe
                  srcDoc={customMessage.trim()
                    ? htmlContent.replace('<!-- Credentials Card -->', `<div style="background:#f0fdfa;border-left:3px solid #4ABFCF;padding:12px 16px;margin:16px 0;border-radius:0 6px 6px 0;"><p style="margin:0;color:#334155;font-size:14px;">${customMessage}</p></div>\n    <!-- Credentials Card -->`)
                    : htmlContent}
                  title="Email Preview"
                  style={{ width: '100%', height: '340px', border: 'none' }}
                  sandbox="allow-same-origin"
                />
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between pt-4 border-t border-slate-100 mt-2">
          <Button variant="ghost" size="sm" onClick={handleSkip} className="text-slate-400 hover:text-slate-600" data-testid="invite-skip-btn">
            <X className="w-3.5 h-3.5 mr-1" /> Skip
          </Button>
          <Button
            size="sm"
            onClick={handleSend}
            disabled={sending || !subject.trim()}
            className="bg-[#4ABFCF] hover:bg-[#3AAAB9] px-6"
            data-testid="invite-send-btn"
          >
            {sending ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1.5" />}
            {sending ? 'Sending...' : 'Send Invite'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
